<template>
  <div class="all-custom-collabs-page">
    <h1 class="global-h1 all-custom-collabs-page__title">
      Custom Collabs
      <BetaBadge class="all-custom-collabs-page__beta-badge" />
    </h1>
    <p>
      A flexible, self-serve collab system for working with creators, influencers, and locals.
      <InfoIconWithTooltip :width="500" style="margin-left: 10px">
        <b>FAQ</b>
        <ul style="margin-top: 5px; padding-left: 20px">
          <li>You invite and coordinate with the creator.</li>
          <li>You pay/comp the creator directly. (in-platform payments coming soon)</li>
          <li>Do as many custom collabs as you want!</li>
          <li>Shrpa Creator visits are a structured alternative to custom collabs.</li>
        </ul>
      </InfoIconWithTooltip>
    </p>

    <div class="all-custom-collabs-page__divider"></div>

    <Loader v-if="isLoading" />

    <template v-else>
      <div class="all-custom-collabs-page__buttons-panel">
        <SrpButton
          @click="
            async () => {
              isNewCustomCollabWizardModalMounted = true;
              await nextTick();
              domRefNewCustomCollabWizard?.openModal();
            }
          "
        >
          <template #icon>
            <IconEmbedded name="plus_3-5" :size="22" />
          </template>
          Start a New Collab
        </SrpButton>

        <NewCustomCollabWizard v-if="isNewCustomCollabWizardModalMounted && customerId" ref="domRefNewCustomCollabWizard" :customerId="customerId" />

        <span style="display: inline-flex">
          <LinkWithIcon color="blue" tag="RouterLink" :to="{ name: 'CustomerSavedCreators', params: { pageId: customerId } }">
            <template #icon><IconEmbedded name="users_2" :size="20" /></template>
            <span>View Saved Creators</span>
          </LinkWithIcon>
        </span>
      </div>

      <!-- Collab snippets section -->
      <div v-if="notCompletedCollabs?.length" class="collab-snippets-section all-custom-collabs-page__collab-snippets-section">
        <ul class="collab-snippets-section__snippets-list">
          <CustomCollabActionSummaryTile
            class="collab-snippets-section__snippet"
            v-for="customCollab in notCompletedCollabs"
            :key="customCollab.customCollabId"
            :customCollabWidgetPayload="customCollab"
            userType="customer"
          />
        </ul>
      </div>
      <!-- / Collab snippets section -->

      <!-- Collab snippets section -->
      <div v-if="completedCollabs?.length" class="collab-snippets-section all-custom-collabs-page__collab-snippets-section">
        <h2 class="global-h2 collab-snippets-section__title">Completed</h2>

        <div class="collab-snippets-section__divider"></div>

        <ul class="collab-snippets-section__snippets-list">
          <CustomCollabActionSummaryTile
            class="collab-snippets-section__snippet"
            v-for="customCollab in completedCollabs"
            :key="customCollab.customCollabId"
            :customCollabWidgetPayload="customCollab"
            userType="customer"
          />
        </ul>
      </div>
      <!-- / Collab snippets section -->
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, inject, watch, nextTick } from "vue";
import CustomerCustomCollabsRepo from "@repos/CustomerCustomCollabsRepo";
import { useRouter, useRoute } from "vue-router";
import { useHead } from "@unhead/vue";
import { RouteHelper } from "@helpers/RouteHelper";
import { MetricSender } from "@/helpers/MetricSender";
import { useUserProfileStore } from "@stores/userProfileStore";
import AdminContext from "@logic/AdminContext";
// Components
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import NewCustomCollabWizard from "../CustomerNewCustomCollabWizard/index.vue";
import CustomCollabActionSummaryTile from "./CustomCollabActionSummaryTile.vue";
import Loader from "@components/Loader/Loader.vue";
import BetaBadge from "@components/BetaBadge.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";

// Types
import { CustomCollabWidgetPayload } from "@contracts/customCollabs";

// Global variables & injections
const globalRoot = inject("globalRoot") as any;

const router = useRouter();
const route = useRoute();
const userProfileStore = useUserProfileStore();

// Mount the NewCustomCollabWizardModal by demand =============================
const isNewCustomCollabWizardModalMounted = ref<boolean>(false);

// Set browser title ==========================================================
onMounted(() => {
  const title = `Custom Collabs - ${globalRoot?.orgInContext?.name ?? "Shrpa"}`;
  useHead({ title: () => title, meta: [{ name: "description", content: title }] });

  MetricSender.sendMetric("Cust-ViewCustomCollabs", null);
});

// DOM refs ===================================================================
const domRefNewCustomCollabWizard = ref<any>(null);

// Load data ==================================================================
const isLoading = ref<boolean>(true);
const allCustomCollabs = ref<Array<CustomCollabWidgetPayload>>([]);

let customerId = ref<string>("");

onMounted(async () => {
  isLoading.value = true;
  customerId.value = String(route.params.communityId);
  await AdminContext.setOrgInContextFromCustomerId(customerId.value);
  allCustomCollabs.value = await CustomerCustomCollabsRepo.getAllCustomCollabsV2(customerId.value, userProfileStore.getViewingCreatorIdIfImpersonating);
  allCustomCollabs.value = allCustomCollabs.value.filter(c => Boolean(c));
  isLoading.value = false;
});

// // Filter collabs list ========================================================
const notCompletedCollabs = computed<Array<CustomCollabWidgetPayload>>(() => {
  return allCustomCollabs.value.filter(c => !c.markedCompleteDate);
});
const completedCollabs = computed<Array<CustomCollabWidgetPayload>>(() => {
  return allCustomCollabs.value.filter(c => c.markedCompleteDate);
});

// Check for "createdForCreatorId" flag =======================================
watch(isLoading, async () => {
  if (!isLoading.value) {
    if (RouteHelper.getHashParam("createdForCreatorId")) {
      const createdForCreatorId = RouteHelper.getHashParam("createdForCreatorId");
      // removes the "createdForCreatorId" hash value to prevent the creation of another identical Collab on reload
      const hashParams = new URLSearchParams(route.hash.substring(1));
      hashParams.delete("createdForCreatorId");
      await router.replace({ ...route, hash: `#${hashParams.toString()}` });

      isNewCustomCollabWizardModalMounted.value = true;
      await nextTick();
      await domRefNewCustomCollabWizard.value.openModal(createdForCreatorId);
    }
  }
});

// Check for "openNewCollabModal" flag ========================================
watch(isLoading, async () => {
  if (!isLoading.value) {
    if (RouteHelper.getHashParam("openNewCollabModal")) {
      // removes the "openNewCollabModal" hash value to prevent the creation of another identical Collab on reload
      const hashParams = new URLSearchParams(route.hash.substring(1));
      hashParams.delete("openNewCollabModal");
      await router.replace({ ...route, hash: `#${hashParams.toString()}` });

      isNewCustomCollabWizardModalMounted.value = true;
      await nextTick();
      await domRefNewCustomCollabWizard.value.openModal();
    }
  }
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Collab snippets section ====================================================
.collab-snippets-section {
  &__title {
    margin-bottom: 20px;
  }

  &__divider {
    margin-bottom: 30px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__snippets-list {
    gap: $desktop-wide-grid-gap-width;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    list-style: none;
  }

  &__snippet {
    width: calc(50% - $desktop-wide-grid-gap-width / 2);
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .collab-snippets-section {
    &__snippet {
      width: 100%;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .collab-snippets-section {
    &__snippet {
      width: 100%;
    }
  }
}

// All Custom Collabs page ====================================================
.all-custom-collabs-page {
  width: $desktop-wide-right-col-width;
  margin: 0 auto;

  &__title {
    margin-bottom: 16px;
  }

  &__beta-badge {
    vertical-align: top;
  }

  &__divider {
    width: 100%;
    margin-bottom: 35px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__buttons-panel {
    gap: 17px 40px;
    margin-bottom: 50px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  &__collab-snippets-section {
    margin-bottom: 60px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .all-custom-collabs-page {
    width: $desktop-right-col-width;
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .all-custom-collabs-page {
    width: $laptop-right-col-width;
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .all-custom-collabs-page {
    width: $tablet-large-content-width;
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .all-custom-collabs-page {
    width: $tablet-content-width;
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .all-custom-collabs-page {
    width: $mobile-content-width;
  }
}
</style>
